(() => {
  // Can't reply, hands on clay: las escenas avanzan con el scroll y suben los mensajes sin leer. Fijado en escritorio; apilado en móvil y con movimiento reducido.
  // Clon de la máquina de It's a long story (cat-long-story.js): el contador de páginas pasa a ser el de sin leer.
  const mount = (root) => {
    if (root.dataset.crReady) return;
    const track = root.querySelector('[data-cr-track]');
    const stage = root.querySelector('[data-cr-stage]');
    const scenes = [...root.querySelectorAll('[data-cr-scene]')];
    if (!track || !stage || !scenes.length) return;
    root.dataset.crReady = 'true';
    const labels = [...root.querySelectorAll('[data-cr-label-item]')];
    const tabs = [...root.querySelectorAll('[data-cr-tab]')];
    const messages = [...root.querySelectorAll('[data-cr-msg]')];
    const reply = root.querySelector('[data-cr-reply]');
    const chat = root.querySelector('[data-cr-chat]');
    const count = root.querySelector('[data-cr-count]');
    const unreadNumber = root.querySelector('[data-cr-unread]');
    const share = root.querySelector('[data-cr-share]');
    const note = root.querySelector('[data-cr-note]');
    const total = scenes.length;
    const unreadAt = (index) => Number(scenes[index].dataset.unread) || 0;
    const lastUnread = unreadAt(total - 1) || 1;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    const wide = window.matchMedia('(min-width: 1100px) and (min-height: 640px)');
    let current = -1;
    let pinned = false;
    let frame = 0;
    let countFrame = 0;
    let shown = unreadAt(0);
    let chatInView = false;
    let sceneObserver = null;
    let chatObserver = null;

    root.style.setProperty('--cr-n', total);
    scenes.forEach((scene, index) => scene.style.setProperty('--k', index));

    // Los sin leer suben deprisa hasta los de la escena nueva
    const countTo = (target) => {
      cancelAnimationFrame(countFrame);
      if (!unreadNumber) return;
      if (reduced.matches) { shown = target; unreadNumber.textContent = String(target); return; }
      const from = shown;
      const start = performance.now();
      const step = (now) => {
        const t = Math.min(1, (now - start) / 900);
        shown = Math.round(from + (target - from) * (1 - Math.pow(1 - t, 3)));
        unreadNumber.textContent = String(shown);
        if (t < 1) countFrame = requestAnimationFrame(step);
      };
      countFrame = requestAnimationFrame(step);
    };

    // Los mensajes llegan con su escena; la respuesta automática, solo al final. El globo del chat lleva los mismos sin leer que el contador.
    const updateChat = () => {
      const show = pinned || chatInView;
      messages.forEach((message) => message.classList.toggle('is-in', show && Number(message.dataset.crMsg) <= current));
      const final = show && current === total - 1;
      if (reply) reply.classList.toggle('is-in', final);
      root.classList.toggle('is-final', final);
      const badge = String(unreadAt(Math.max(current, 0)));
      if (count && count.textContent !== badge) {
        count.textContent = badge;
        count.classList.add('is-bump');
        window.setTimeout(() => count.classList.remove('is-bump'), 280);
      }
    };

    const setScene = (index) => {
      if (index === current) return;
      current = index;
      root.dataset.scene = String(index);
      scenes.forEach((scene, i) => {
        scene.classList.toggle('is-active', i === index);
        scene.classList.toggle('is-past', i < index);
      });
      labels.forEach((label, i) => label.classList.toggle('is-active', i === index));
      tabs.forEach((tab, i) => tab.setAttribute('aria-current', i === index ? 'true' : 'false'));
      const value = unreadAt(index);
      root.style.setProperty('--cr-p', (value / lastUnread).toFixed(4));
      countTo(value);
      updateChat();
    };

    const progress = () => {
      const top = parseFloat(getComputedStyle(stage).top) || 0;
      const distance = track.offsetHeight - stage.offsetHeight;
      return Math.min(1, Math.max(0, (top - track.getBoundingClientRect().top) / Math.max(distance, 1)));
    };

    const update = () => {
      frame = 0;
      if (!pinned) return;
      setScene(Math.min(total - 1, Math.floor(progress() * total)));
    };
    const onScroll = () => { if (!frame) frame = requestAnimationFrame(update); };

    // Apilado: la escena activa es la última cuyo borde de arriba ya ha pasado la franja central. Se recalcula en cada aviso
    // del observador (también cuando una escena sale), así un scroll rápido que se salta una escena entera no deja la barra atrasada.
    const pickStacked = () => {
      const line = window.innerHeight * 0.58;
      let index = 0;
      scenes.forEach((scene, i) => { if (scene.getBoundingClientRect().top < line) index = i; });
      setScene(index);
    };

    const observeStacked = () => {
      sceneObserver = new IntersectionObserver(pickStacked, { rootMargin: '-42% 0px -42% 0px' });
      scenes.forEach((scene) => sceneObserver.observe(scene));
      if (chat) {
        chatObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            chatInView = true;
            setScene(total - 1);
            updateChat();
            chatObserver.disconnect();
          });
        }, { threshold: 0.45 });
        chatObserver.observe(chat);
      }
    };

    const mode = () => {
      const pin = wide.matches && !reduced.matches;
      if (sceneObserver) { sceneObserver.disconnect(); sceneObserver = null; }
      if (chatObserver) { chatObserver.disconnect(); chatObserver = null; }
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      pinned = pin;
      root.classList.toggle('is-pinned', pin);
      root.classList.toggle('is-stacked', !pin);
      if (pin) {
        window.addEventListener('scroll', onScroll, { passive: true });
        window.addEventListener('resize', onScroll, { passive: true });
        update();
      } else {
        observeStacked();
      }
      updateChat();
    };

    tabs.forEach((tab) => tab.addEventListener('click', (event) => {
      if (!pinned) return;
      event.preventDefault();
      const index = Number(tab.dataset.crTab);
      const top = parseFloat(getComputedStyle(stage).top) || 0;
      const distance = track.offsetHeight - stage.offsetHeight;
      const target = window.scrollY + track.getBoundingClientRect().top - top + ((index + 0.5) / total) * distance;
      window.scrollTo({ top: target, behavior: reduced.matches ? 'auto' : 'smooth' });
    }));

    // La respuesta automática se copia con el enlace de la camiseta. Sin portapapeles, hoja de compartir del sistema;
    // si tampoco hay, el texto se queda a la vista en la nota para copiarlo a mano.
    if (share) {
      share.addEventListener('click', async () => {
        const text = share.dataset.text || '';
        const url = share.dataset.url || window.location.origin;
        try {
          await navigator.clipboard.writeText(`${text} ${url}`);
          if (note) note.textContent = share.dataset.copied || '';
          return;
        } catch (error) { /* sin portapapeles: se prueba a compartir */ }
        if (navigator.share) {
          try { await navigator.share({ text, url }); return; } catch (error) { if (error && error.name === 'AbortError') return; }
        }
        if (note) note.textContent = `${text} ${url}`;
      });
    }

    root.classList.add('is-live');
    setScene(0);
    mode();
    wide.addEventListener('change', mode);
    reduced.addEventListener('change', mode);
  };

  const init = (scope) => scope.querySelectorAll('[data-cr-story]').forEach(mount);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  else init(document);
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
